import { FileActionButton } from "../file-action/file-action-controls";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { ColumnDef } from "../../models/orders/order-list-model";
import { useState, Suspense, useMemo, useImperativeHandle, forwardRef } from "react";
import posService from "../../services/pos/pos.service";
import AppLoading from "../app-loading/app-loading";
import SummaryTableInDetailsDatatable from "./pos-movement-summary-indetaildatatable";
import SummaryTableSalesInDetailsDatatable from "./pos-movement-summary-salesindetaildatatable";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
 
 
interface Summarylog {
  productName: string;
  inwardQuantity: number;
  outwardQuantity: number;
  adjustQuantity: number;
  salesQuantity: number;
  createdDate: any;
  productId: number;
}
 
interface SummaryTableProps {
  data: Summarylog[];
  loading: boolean;
  posId: number;
  startDate: any;
  endDate: any;
}
 
const SummaryTable = forwardRef<{ resetFilters: () => void }, SummaryTableProps>(
  ({ data, loading, posId, startDate, endDate }, ref) => {
 
    const [visible, setVisible] = useState<boolean>(false);
    const [inDetails, setInDetails] = useState<any[]>([]);
    const [dailogHeader, setDailogHeader] = useState<string>("");
    const [type, setType] = useState<string>("");
    const [productFilter, setProductFilter] = useState<string[]>([]);
 
    // Sort state
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<1 | -1>(1); // 1 = asc, -1 = desc
 
    // Expose reset function to parent component
    useImperativeHandle(ref, () => ({
      resetFilters: () => {
        setProductFilter([]);
        setSortField(null);
        setSortOrder(1);
      }
    }));
 
    // Get unique products for filter
    const uniqueProducts = useMemo(() => {
      const products = data.map(item => item.productName).filter(Boolean);
      return Array.from(new Set(products)).sort();
    }, [data]);
 
    // Filter data based on product filter and apply sorting
    const filteredData = useMemo(() => {
      let filtered = [...data];
 
      if (productFilter.length > 0) {
        filtered = filtered.filter(item =>
          productFilter.includes(item.productName)
        );
      }
 
      // Apply sorting if a sort field is selected
      if (sortField) {
        filtered = [...filtered].sort((a, b) => {
          const aVal = a[sortField as keyof Summarylog];
          const bVal = b[sortField as keyof Summarylog];
 
          if (aVal === null || aVal === undefined) return 1;
          if (bVal === null || bVal === undefined) return -1;
 
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            return sortOrder * aVal.localeCompare(bVal);
          }
 
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return sortOrder * (aVal - bVal);
          }
 
          return 0;
        });
      }
 
      return filtered;
    }, [data, productFilter, sortField, sortOrder]);
 
    // Clear all filters
    const clearFilters = () => {
      setProductFilter([]);
      setSortField(null);
      setSortOrder(1);
    };
 
    // Check if any filters are active
    const hasActiveFilters = productFilter.length > 0;
 
    // Export function to convert data to CSV and download
    const exportToCSV = () => {
      if (filteredData.length === 0) {
        return;
      }
 
      // Define CSV headers
      const headers = ['Product', 'Inward', 'Outward', 'Adjust', 'Sales'];
 
      // Convert data to CSV rows
      const csvRows = filteredData.map(row => {
        return [
          row.productName || '',
          row.inwardQuantity?.toString() || '0',
          row.outwardQuantity?.toString() || '0',
          row.adjustQuantity?.toString() || '0',
          row.salesQuantity?.toString() || '0'
        ].map(field => {
          // Escape fields that contain commas, quotes, or newlines
          const escaped = field.replace(/"/g, '""');
          return escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')
            ? `"${escaped}"`
            : escaped;
        }).join(',');
      });
 
      // Combine headers and rows
      const csvContent = [headers.join(','), ...csvRows].join('\n');
 
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
 
      link.setAttribute('href', url);
      link.setAttribute('download', `summary_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
 
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
 
    // Filterable Column Header Component
    const FilterableColumnHeader = ({
      title,
      filterValue,
      onFilterChange,
      options,
      sortField: columnField,
      showSort = true
    }: {
      title: string;
      filterValue: string[];
      onFilterChange: (value: string[]) => void;
      options: string[];
      sortField?: string;
      showSort?: boolean;
    }) => {
      const isCurrentlySorted = sortField === columnField;
      const activeFilterCount = filterValue.length;
 
      const handleSort = () => {
        if (sortField === columnField) {
          // Toggle sort order
          setSortOrder(sortOrder === 1 ? -1 : 1);
        } else {
          // New field, set to ascending
          setSortField(columnField || null);
          setSortOrder(1);
        }
      };
 
      return (
        <div className="flex align-items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
          <span className="font-semibold">{title}</span>
 
          <div className="flex align-items-center gap-1">
            {/* Filter dropdown */}
            <div className="p-overlay-badge">
              <MultiSelect
                value={filterValue}
                options={options}
                onChange={(e) => onFilterChange(e.value)}
                placeholder={`Filter ${title}`}
                maxSelectedLabels={0}
                selectedItemsLabel={`${activeFilterCount} selected`}
                className="custom-multiselect"
                style={{
                  width: '40px',
                  height: '32px',
                }}
                panelStyle={{ minWidth: '200px' }}
                display="chip"
                pt={{
                  root: {
                    style: {
                      border: activeFilterCount > 0 ? '2px solid #22c55e' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: activeFilterCount > 0 ? '#f0fdf4' : 'white',
                      transition: 'all 0.3s ease',
                    }
                  },
                  trigger: {
                    style: {
                      width: '100%',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }
                  },
                  label: {
                    style: { display: 'none' }
                  }
                }}
              >
                <template>
                  <div className="flex align-items-center justify-content-center" style={{ width: '100%' }}>
                    <i
                      className="pi pi-filter"
                      style={{
                        fontSize: '0.9rem',
                        color: activeFilterCount > 0 ? '#22c55e' : '#6b7280'
                      }}
                    />
                    {activeFilterCount > 0 && (
                      <span
                        className="ml-1 badge-custom"
                        style={{
                          background: '#22c55e',
                          color: 'white',
                          borderRadius: '10px',
                          padding: '0 5px',
                          fontSize: '0.65rem',
                          fontWeight: 'bold',
                          minWidth: '16px',
                          textAlign: 'center'
                        }}
                      >
                        {activeFilterCount}
                      </span>
                    )}
                  </div>
                </template>
              </MultiSelect>
            </div>
 
            {/* Sort button */}
            {showSort && columnField && (
              <Button
                icon={isCurrentlySorted && sortOrder === -1 ? "pi pi-sort-amount-down" : "pi pi-sort-amount-up"}
                className="p-button-text p-button-sm"
                onClick={handleSort}
                style={{
                  color: isCurrentlySorted ? '#22c55e' : '#6b7280',
                  padding: '4px 8px',
                  minWidth: 'auto',
                  transition: 'all 0.3s ease'
                }}
                tooltip={`Sort ${isCurrentlySorted ? (sortOrder === 1 ? 'Descending' : 'Ascending') : 'Ascending'}`}
                tooltipOptions={{ position: 'top' }}
              />
            )}
          </div>
        </div>
      );
    };
 
    function handelInwardClick() {
      setVisible(false)
    }
 
    const handelInwardQuantityClick = async (quantity: number, productId: number) => {
 
      if (quantity > 0) {
        setType("Inward");
        loading = true;
        setVisible(true);
        var filter = {
          "posId": posId,
          "startDate": startDate,
          "endDate": endDate,
          "productId": productId,
          "typeName": "Inward"
        }
        const response = await posService.GetPOSMovementsInwardDetails(filter)
        if (response.length > 0) {
          setInDetails(response);
          setDailogHeader("Inward Details for " + response[0].posName + "- " + data.filter(x => x.productId === productId)[0].productName);
          loading = false;
        }
      }
      else {
        setInDetails([]);
      }
    }
 
    const handelOutwardQuantityClick = async (quantity: number, productId: number) => {
 
      if (quantity > 0) {
        setType("Outward");
        loading = true;
        setVisible(true);
        var filter = {
          "posId": posId,
          "startDate": startDate,
          "endDate": endDate,
          "productId": productId,
          "typeName": "Outward"
        }
        const response = await posService.GetPOSMovementsInwardDetails(filter)
        if (response.length > 0) {
          setInDetails(response);
          setDailogHeader("Outward Details for " + response[0].posName + "- " + data.filter(x => x.productId === productId)[0].productName);
          loading = false;
        }
      }
      else {
        setInDetails([]);
      }
    }
 
    const handelSalesQuantityClick = async (quantity: number, productId: number) => {
      if (quantity > 0) {
        setType("Sales");
        loading = true;
        setVisible(true);
        var filter = {
          "posId": posId,
          "startDate": startDate,
          "endDate": endDate,
          "productId": productId,
        }
        const response = await posService.GetPOSMovementsSalesInwardDetails(filter)
        if (response.length > 0) {
          setInDetails(response);
          setDailogHeader("Sales Details for " + response[0].posName + "- " + data.filter(x => x.productId === productId)[0].productName);
          loading = false;
        }
      }
      else {
        setInDetails([]);
      }
    }
 
    const columns: ColumnDef[] = [
      {
        field: "productName",
        header: "Product",
        style: { minWidth: "9rem" },
        sortable: true,
      },
      {
        field: "inwardQuantity",
        header: "Inward",
        style: { minWidth: "7rem" },
        sortable: true,
        cell: (row: any) => (
          row.inwardQuantity > 0 ?
            <span
              style={{ color: "darkcyan", fontWeight: "bolder", cursor: "pointer" }}
              onClick={() => handelInwardQuantityClick(row.inwardQuantity, row.productId)}
            >
              {row.inwardQuantity}
            </span> : <span>{row.inwardQuantity}</span>
        ),
      },
      {
        field: "outwardQuantity",
        header: "Outward",
        style: { minWidth: "9rem" },
        sortable: true,
        cell: (row: any) => (
          row.outwardQuantity > 0 ?
            <span
              style={{ color: "darkcyan", fontWeight: "bolder", cursor: "pointer" }}
              onClick={() => handelOutwardQuantityClick(row.outwardQuantity, row.productId)}
            >
              {row.outwardQuantity}
            </span> : <span>{row.outwardQuantity}</span>
        ),
      },
      {
        field: "adjustQuantity",
        header: "Adjust",
        style: { minWidth: "9rem" },
        sortable: true,
      },
      {
        field: "salesQuantity",
        header: "Sales",
        style: { minWidth: "9rem" },
        sortable: true,
        cell: (row: any) => (
          row.salesQuantity > 0 ?
            <span
              style={{ color: "darkcyan", fontWeight: "bolder", cursor: "pointer" }}
              onClick={() => handelSalesQuantityClick(row.salesQuantity, row.productId)}
            >
              {row.salesQuantity}
            </span> : <span>{row.salesQuantity}</span>
        ),
      },
    ];
 
    return (
 
      <div className="col-12">
        {/* Section Header */}
        <div className="flex align-items-center gap-2 mb-3 px-2">
          <div style={{
            backgroundColor: 'rgb(76, 175, 80)',
            borderRadius: '8px',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="pi pi-chart-bar" style={{ color: 'white', fontSize: '1.2rem' }}></i>
          </div>
          <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '1.1rem' }}>Summary Records</span>
        </div>
 
        <Suspense fallback={<AppLoading />}>
          <div className="card p-2" style={{ borderRadius: '12px' }}>
            {/* Header Actions: Export and Clear Filters */}
            {(filteredData.length > 0 || hasActiveFilters) && (
              <div className="flex justify-content-end mb-2 gap-2">
                {/* Export Button */}
                {filteredData.length > 0 && (
                  <>
                    <Tooltip target=".export-btn-summary-tooltip" content={`Export ${filteredData.length} record(s)`} position="top" />
                    <FileActionButton action="export"
                      label="Export"
                      icon="pi pi-download"
                      onClick={exportToCSV}
                      className="p-button-sm export-btn-summary-tooltip"
                      style={{
                        backgroundColor: 'rgb(76, 175, 80)',
                        border: 'none',
                        borderRadius: '6px',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        fontSize: '0.9rem'
                      }}
                    />
                  </>
                )}
 
                {/* Clear Filters Button */}
                {hasActiveFilters && (
                  <Button
                    icon="pi pi-filter-slash"
                    label="Clear All Filters"
                    onClick={clearFilters}
                    outlined
                    style={{
                      borderRadius: '6px',
                      padding: '0.5rem 1rem',
                      fontSize: '0.9rem',
                      color: '#64748b',
                      borderColor: '#cbd5e1'
                    }}
                  />
                )}
              </div>
            )}
 
            <DataTable
              value={filteredData}
              loading={loading}
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 20, 50]}
              paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
              currentPageReportTemplate="{first} to {last} of {totalRecords}"
            >
              {/* Product Column with Filterable Header */}
              <Column
                field="productName"
                header={
                  <FilterableColumnHeader
                    title="Product"
                    filterValue={productFilter}
                    onFilterChange={setProductFilter}
                    options={uniqueProducts}
                    sortField="productName"
                  />
                }
                style={{ minWidth: "9rem" }}
              />
 
              {/* Remaining Columns */}
              {columns.slice(1).map((col, i) => (
                <Column
                  key={i}
                  field={col.field}
                  header={col.header}
                  sortable={col.sortable}
                  style={col.style}
                  body={col.cell}
                />
              ))}
            </DataTable>
          </div>
          <Dialog visible={visible} style={{ width: "70vw" }} header={dailogHeader} onHide={handelInwardClick}>
            {inDetails.length > 0 ?
              type === "Inward" || type === "Outward" ?
                <SummaryTableInDetailsDatatable data={inDetails} type={type} /> :
                <SummaryTableSalesInDetailsDatatable data={inDetails} /> : ""
            }
          </Dialog>
          {loading && <AppLoading />}
        </Suspense>
      </div>
 
    );
 
  }
);
 
export default SummaryTable;
 